import Vue from 'vue';
import Modal from 'components/modals/Modal.vue';
import CustomModal from 'components/modals/CustomModal.vue';
import AdUnitsPendingApprovalModal from '@/pages/monetize/adUnitSettings/PendingApprovalModal.vue';
import {
    generateId,
    prepareContainer
} from 'services/overlayService/OverlayService';
import consts from 'services/metaData/consts';
import StartQuickABModal from '@/pages/monetize/abTest/StartQuickABModal.vue';
import generalConfig from 'services/metaData/generalConfig';

const config = generalConfig();

// A map to hold the currently open modals
export const modals = new Map();

Vue.use({
    install(v) {
        // eslint-disable-next-line no-param-reassign
        v.prototype.$modal = {
            // this function is needed as angular isn't aware of Custom Modals -
            // refactor to this.open after modals are no longer called from external components
            openStartQuickABModal(options) {
                return this.open({ ...options, component: StartQuickABModal });
            },
            openPendingApprovalModal(options) {
                return this.open({ ...options, component: AdUnitsPendingApprovalModal });
            },
            open({
                type,
                body,
                title,
                props = {},
                okButtonText,
                okButtonWidth,
                cancelButtonText,
                onOK,
                onClose,
                container,
                showCloseButton = true,
                showCancelButton = true,
                clickOutsideClose = true,
                animate = true,
                version = 2,
                showIcon = true,
                id = generateId(),
                component = Modal
            }) {
                const modalScope = this;
                const { vueContainer } = prepareContainer(container);

                const modal = new Vue({
                    el: vueContainer.firstChild,
                    data() {
                        return {
                            body,
                            title,
                            okButtonText,
                            cancelButtonText,
                            type,
                            onOK,
                            onClose,
                            loading: false,
                            open: !animate,
                            showCloseButton,
                            showCancelButton,
                            clickOutsideClose,
                            version,
                            showIcon,
                            id,
                            component,
                            okButtonWidth,
                            errorMessage: ''
                        };
                    },
                    render(createElement) {
                        return createElement(this.component, {
                            tag: 'component',
                            props: {
                                open: this.open,
                                id: this.id,
                                version: this.version,
                                showIcon: this.showIcon,
                                loading: this.loading,
                                type: this.type,
                                clickOutsideClose: this.clickOutsideClose,
                                okButtonText: this.okButtonText,
                                cancelButtonText: this.cancelButtonText,
                                showCloseButton: this.showCloseButton,
                                showCancelButton: this.showCancelButton,
                                body: this.body,
                                title: this.title,
                                okButtonWidth: this.okButtonWidth,
                                errorMessage: this.errorMessage,
                                ...props
                            },
                            on: {
                                cancel: this.close,
                                ok: this.ok,
                                errorMessageAcknowledged() {
                                    modal.errorMessage = '';
                                }
                            }
                        });
                    },
                    methods: {
                        animateClose() {
                            this.open = false;

                            if (animate) {
                                setTimeout(() => modalScope.close(id), 100);
                            } else {
                                modalScope.close(id);
                            }
                        },
                        close(...args) {
                            if (onClose) {
                                onClose(...args);
                            }

                            this.animateClose();
                        },
                        async ok(...args) {
                            if (!onOK) {
                                this.animateClose();
                                return;
                            }

                            this.loading = true;
                            try {
                                const shouldClose = await onOK(...args);
                                const okReturnedValue = typeof shouldClose !== 'undefined';

                                // if onOk returns false, prevent closing the modal
                                if (okReturnedValue && !shouldClose) {
                                    this.loading = false;
                                    return;
                                }
                            } catch (error) {
                                this.errorMessage = error?.response?.data?.error
                                    || error?.response?.data?.errorMessage
                                    || consts.GENERAL_SERVICE_ERROR;
                                this.loading = false;
                                // eslint-disable-next-line no-console
                                if (!config.isProduction) console.error(error);
                                return;
                            }

                            this.animateClose();
                        }
                    }
                });

                if (animate) {
                    modal.$nextTick(() => { modal.open = true; });
                }

                // add the modal to the modals map so it can be accessed with it's id
                modals.set(id, {
                    modal,
                    vueContainer
                });

                return id;
            },
            close(id) {
                let modalToRemove;
                let modalId = id;

                // if an id was provided, get the modal by id, if not, get the last opened modal
                if (modalId) {
                    modalToRemove = modals.get(id);
                } else {
                    const lastOpenModal = [...modals].pop();
                    [modalId, modalToRemove] = lastOpenModal;
                }

                if (modalToRemove) {
                    modals.delete(modalId);

                    const {
                        modal,
                        vueContainer
                    } = modalToRemove;

                    modal.$destroy();
                    modal.$el.remove();
                    vueContainer.remove();
                }
            }
        };
    }
});
